
import React, { useRef, useLayoutEffect } from 'react';
import { WorkItem, ReferenceAsset, GeneratedImage } from '../types';
import { RefreshCw, X, Plus, Edit2, Sparkles, AlertCircle } from 'lucide-react';
import { ImageGrid } from './ImageGrid';

interface WorkItemCardProps {
  item: WorkItem;
  assets: ReferenceAsset[];
  editingId: string | null;
  editTempText: string;
  onEditStart: (id: string, text: string) => void;
  onEditChange: (text: string) => void;
  onEditSave: () => void;
  onAssetRemove: (itemId: string, assetId: string) => void;
  onAssetAdd: (itemId: string) => void;
  onRegenerate: (itemId: string) => void;
  onViewImage: (img: GeneratedImage) => void;
  onEditImage: (img: GeneratedImage) => void;
}

export const WorkItemCard: React.FC<WorkItemCardProps> = ({
  item, assets, editingId, editTempText, onEditStart, onEditChange, onEditSave,
  onAssetRemove, onAssetAdd, onRegenerate, onViewImage, onEditImage
}) => {
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // useLayoutEffect runs synchronously after DOM mutations but before the browser paints.
  // This prevents the visual "flash" or "jump" where the textarea starts small and grows.
  useLayoutEffect(() => {
    if (editingId === item.id && editInputRef.current) {
      const textarea = editInputRef.current;
      // Reset height momentarily to get the correct scrollHeight if text shrunk
      textarea.style.height = 'inherit'; 
      
      // Calculate new height
      const scrollHeight = textarea.scrollHeight;
      
      // Apply new height
      textarea.style.height = `${scrollHeight}px`;
    }
  }, [editingId, editTempText, item.id]);

  const getStatusLabel = (s: string) => {
    switch(s) { case 'idle': return '空闲'; case 'generating': return '生成中'; case 'completed': return '完成'; case 'failed': return '失败'; default: return s; }
  };

  const getFriendlyError = (error?: string) => {
      if (!error) return "生成失败";
      if (error === "PROHIBITED_CONTENT") return "内容合规性拦截：提示词或参考图包含违规信息";
      if (error === "TIMEOUT") return "服务响应超时：请检查网络或稍后重试";
      return error;
  }

  // Shared text styles to ensure 1:1 visual match between display and edit modes
  const commonTextStyles = "w-full text-sm leading-normal rounded-lg p-2 border font-sans transition-colors max-h-[100px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent";

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden shadow-sm transition-all hover:border-slate-700">
      <div className="p-3 flex flex-col sm:flex-row items-start gap-3 bg-slate-900/80">
        
        {/* ID / Index Badge */}
        <div className="flex items-center justify-between w-full sm:w-auto">
            <div className="flex-shrink-0 w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center border border-slate-700 text-slate-400 font-mono text-xs font-bold">
            {item.index}
            </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 min-w-0 w-full">
          {/* Reference Image Stitching Prompt Display */}
          {item.referenceDescription && (
             <div className="mb-1.5 px-1 flex items-start gap-1.5">
               <Sparkles size={10} className="text-indigo-400 mt-0.5 flex-shrink-0" />
               <p className="text-[10px] text-indigo-400 leading-tight font-mono opacity-90 break-all">
                 {item.referenceDescription}
               </p>
             </div>
          )}

          {editingId === item.id ? (
            <textarea 
                ref={editInputRef} 
                className={`${commonTextStyles} bg-slate-950 border-indigo-500 text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-none block`}
                value={editTempText} 
                onChange={(e) => onEditChange(e.target.value)} 
                onBlur={onEditSave} 
                onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEditSave(); } }} 
                autoFocus 
                spellCheck={false}
            />
          ) : (
            <div 
                onClick={() => onEditStart(item.id, item.prompt)} 
                className={`${commonTextStyles} cursor-text bg-transparent border-transparent hover:bg-slate-800/50 hover:border-slate-700/50 group relative`}
            >
                <p className="text-slate-200 whitespace-pre-wrap break-words">{item.prompt}</p>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity sticky">
                    <Edit2 size={14} className="text-slate-500" />
                </div>
            </div>
          )}

          {/* Reference Tags */}
          <div className="mt-2 bg-slate-950/50 p-1.5 rounded-lg border border-slate-800 flex items-center gap-2 flex-wrap">
             <span className="text-[10px] text-slate-500 font-bold uppercase mr-1 select-none flex-shrink-0">参考图:</span>
             {item.selectedReferenceIds.map(refId => {
                 const ref = assets.find(a => a.id === refId);
                 if (!ref) return null;
                 return (
                    <div key={ref.id} className="group flex items-center gap-2 bg-slate-800 border border-slate-700 pr-2 pl-1 py-1 rounded-md max-w-full">
                        <img src={ref.url} className="w-5 h-5 object-cover rounded-sm flex-shrink-0" alt="" />
                        <span className="text-xs text-slate-300 truncate max-w-[80px] sm:max-w-[120px]">{ref.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); onAssetRemove(item.id, ref.id); }} className="ml-1 text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"><X size={12} /></button>
                    </div>
                 );
             })}
             <button onClick={(e) => { e.stopPropagation(); onAssetAdd(item.id); }} className="flex items-center gap-1 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 border-dashed px-2 py-1 rounded-md text-xs transition-all flex-shrink-0 whitespace-nowrap"><Plus size={12} /> 添加</button>
          </div>
          
          {/* Error Message Display */}
          {item.status === 'failed' && (
              <div className="mt-2 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
                  <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
                  <p className="text-[10px] font-medium text-red-400 leading-tight">
                      {getFriendlyError(item.errorMessage)}
                  </p>
              </div>
          )}
        </div>

        {/* Status & Actions Column */}
        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 w-full sm:w-auto justify-between sm:justify-start mt-2 sm:mt-0 border-t sm:border-t-0 border-slate-800/50 pt-3 sm:pt-0">
           <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${item.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : item.status === 'generating' ? 'bg-blue-500/10 text-blue-400 animate-pulse' : item.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-slate-700 text-slate-400'}`}>{getStatusLabel(item.status)}</span>
           <button onClick={() => onRegenerate(item.id)} disabled={item.status === 'generating'} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1.5 hover:underline disabled:opacity-50 px-2 py-1 rounded hover:bg-indigo-500/10 transition-colors"><RefreshCw size={14} /> 重新生成</button>
        </div>
      </div>
      {item.generatedImages.length > 0 && <ImageGrid images={item.generatedImages} index={item.index} onView={onViewImage} onEdit={onEditImage} />}
    </div>
  );
};
